import { Dialog, DialogContent, IconButton, useTheme } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useState, useEffect } from "react";
import StepHeader from "./StepHeader";
import BrandModelStep from "./BrandModelStep";
import UnitDetailsStep from "./UnitDetailsStep";

import { tokens } from "../../../theme";
import type {
  IEquipment,
  AddEquipmentFormData,
  BrandModelFormData,
  EquipmentUnitFormData,
} from "../../../types/equipment";
import SupplierStep from "./Supplier";

interface AddEquipmentDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: AddEquipmentFormData) => Promise<void>;
  equipment?: IEquipment | null;
  isEdit: boolean;
  initialData?: Partial<AddEquipmentFormData> | null;
}

const initialBrandModel: BrandModelFormData = {
  brand_model_id: "",
  brand: "",
  model: "",
  category_id: "",
  manufacturer: "",
  description: "",
  length: "",
  height: "",
  width: "",
  weight: "",
  measuring_unit: "cm",
  power_draw: "",
  current_draw: "",
  rental_value: "",
};

const initialUnit: EquipmentUnitFormData = {
  serial_number: "",
  asset_tag: "",
  status: "available",
  condition: "good",
  location: "",
  description: "",
  purchase_date: "",
  warranty_expiry_date: "",
  purchase_cost: "",
  current_value: "",
  supplier_name: "",
  contact_name: "",
  supplier_phone: "",
  supplier_email: "",
  supplier_website: "",
};

type StepType = 1 | 2 | 3;

export default function AddEquipmentDialog({
  open,
  onClose,
  onSubmit,
  equipment,
  isEdit,
  initialData,
}: AddEquipmentDialogProps) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [step, setStep] = useState<StepType>(1);
  const [brandModelData, setBrandModelData] = useState<BrandModelFormData>(initialBrandModel);
  const [unitData, setUnitData] = useState<EquipmentUnitFormData>(initialUnit);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [forceSerialError, setForceSerialError] = useState(false);

  useEffect(() => {
    if (open && equipment && isEdit) {
      const bm = equipment.brand_model;

      setBrandModelData({
        brand_model_id: bm?.id?.toString() || "",
        brand: bm?.brand_name || "",
        model: bm?.model || "",
        
        category_id: bm?.category?.id?.toString() || "",
        manufacturer: bm?.manufacturer || "",
        description: bm?.description || "",
        length: bm?.length?.toString() || "",
        height: bm?.height?.toString() || "",
        width: bm?.width?.toString() || "",
        weight: bm?.weight?.toString() || "",
        measuring_unit: bm?.measuring_unit || "cm",
        power_draw: bm?.power_draw || "",
        current_draw: bm?.current_draw || "",
        rental_value: bm?.rental_value?.toString() || "",
      });
      
      setUnitData({
        serial_number: equipment.serial_number || "",
        asset_tag: equipment.asset_tag || "",
        status: equipment.status || "available",
        condition: equipment.condition || "good",
        location: equipment.location || "",
        description: equipment.description || "",
        purchase_date: equipment.purchase_date || "",
        warranty_expiry_date: equipment.warranty_expiry_date || "",
        purchase_cost: equipment.purchase_cost || "",
        current_value: equipment.current_value || "",
        supplier_name: equipment.supplier_name || "",
        contact_name: equipment.contact_name || "",
        supplier_phone: equipment.supplier_phone || "",
        supplier_email: equipment.supplier_email || "",
        supplier_website: equipment.supplier_website || "",
      });
      setStep(1);
    } else if (open && !isEdit) {
      setBrandModelData({ ...initialBrandModel, ...initialData?.brandModel });
      setUnitData({ ...initialUnit, ...initialData?.unit });
      setStep(1);
    }
    setForceSerialError(false);
  }, [open, equipment, isEdit, initialData]);

  const handleBrandModelChange = (field: keyof BrandModelFormData, value: string) => {
    setBrandModelData((prev) => ({ ...prev, [field]: value }));
  };

  const handleUnitChange = (field: keyof EquipmentUnitFormData, value: string) => {
    setUnitData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!unitData.serial_number.trim()) {
      setStep(2);
      setForceSerialError(true);
      return;
    }
    if (isSubmitting) return;
    setIsSubmitting(true);
    await onSubmit({ brandModel: brandModelData, unit: unitData });
    setIsSubmitting(false);
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    await onSubmit({ brandModel: brandModelData, unit: unitData });
    setIsSubmitting(false);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 5, overflow: "hidden", maxHeight: "90vh" },
      }}
    >
      <IconButton
        onClick={onClose}
        sx={{ position: "absolute", right: 10, top: 10, color: colors.primary[900], zIndex: 10 }}
      >
        <CloseIcon />
      </IconButton>

      <StepHeader step={step} />

      <DialogContent sx={{ pt: 3, pb: 2, px: 4 }}>
        {step === 1 && (
          <BrandModelStep
            formData={brandModelData}
            onChange={handleBrandModelChange}
            onNext={() => setStep(2)}
            onSave={handleSave}
            isEdit={isEdit}
          />
        )}
        {step === 2 && (
          <UnitDetailsStep
            formData={unitData}
            onChange={handleUnitChange}
            onPrevious={() => setStep(1)}
            onNext={() => { setForceSerialError(false); setStep(3); }}
            onSave={handleSave}
            forceSerialError={forceSerialError}
            onSerialErrorCleared={() => setForceSerialError(false)}
          />
        )}
        {step === 3 && (
          <SupplierStep
            formData={unitData}
            onChange={handleUnitChange}
            onPrevious={() => setStep(2)}
            onSubmit={handleSubmit}
            onSave={handleSave}
            isSubmitting={isSubmitting}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}